import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { updateTeam } from '../../../services/teamService';
import { getTeamDeliverables, createDeliverable, updateDeliverable, deleteDeliverable } from '../../../services/deliverableService';
import { format } from 'date-fns';

const EditTeamModal = ({ isOpen, onClose, team, onTeamUpdated }) => {
  const [formData, setFormData] = useState({
    teamName: '',
    teamNumber: '',
    problemStatement: '',
    mentor: '',
    mentorEmail: '',
    cityOfficial: '',
    city: '',
    category: '',
    projectDeadline: ''
  });
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Category options
  const categoryOptions = [
    { value: 'Transportation', label: 'Transportation' },
    { value: 'Energy', label: 'Energy' },
    { value: 'Water Management', label: 'Water Management' },
    { value: 'Waste Management', label: 'Waste Management' },
    { value: 'Public Safety', label: 'Public Safety' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Education', label: 'Education' },
    { value: 'Governance', label: 'Governance' },
    { value: 'Environment', label: 'Environment' },
    { value: 'Economy', label: 'Economy' },
    { value: 'Other', label: 'Other' }
  ];

  // Load team data and deliverables
  useEffect(() => {
    if (team && isOpen) {
      setFormData({
        teamName: team?.teamName || '',
        teamNumber: team?.teamNumber || '',
        problemStatement: team?.problemStatement || '',
        mentor: team?.mentor || '',
        mentorEmail: team?.mentorEmail || '',
        cityOfficial: team?.cityOfficial || '',
        city: team?.city || '',
        category: team?.category || '',
        projectDeadline: team?.finalDueDate ? format(new Date(team.finalDueDate), 'yyyy-MM-dd') : ''
      });
      setError('');
      setSuccessMessage('');
      loadDeliverables();
    }
  }, [team, isOpen]);

  const loadDeliverables = async () => {
    if (!team?.id) return;
    try {
      const data = await getTeamDeliverables(team?.id);
      setDeliverables(data);
    } catch (err) {
      console.error('Error loading deliverables:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAddDeliverable = () => {
    setDeliverables(prev => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        isNew: true,
        deliverableName: '',
        dueDate: '',
        requiredUploadCount: 1
      }
    ]);
  };

  const handleRemoveDeliverable = async (deliverable) => {
    if (deliverable?.isNew) {
      // Just remove from local state
      setDeliverables(prev => prev?.filter(d => d?.id !== deliverable?.id));
    } else {
      // Delete from database
      if (!window.confirm('Are you sure you want to delete this deliverable? This will also delete all associated uploads.')) {
        return;
      }
      try {
        await deleteDeliverable(deliverable?.id);
        setDeliverables(prev => prev?.filter(d => d?.id !== deliverable?.id));
      } catch (err) {
        console.error('Error deleting deliverable:', err);
        setError(err?.message || 'Failed to delete deliverable');
      }
    }
  };

  const handleDeliverableChange = (deliverableId, field, value) => {
    setDeliverables(prev => prev?.map(d => 
      d?.id === deliverableId ? { ...d, [field]: value } : d
    ));
  };

  const validateForm = () => {
    if (!formData?.teamName?.trim()) {
      setError('Team name is required');
      return false;
    }
    if (!formData?.teamNumber) {
      setError('Team number is required');
      return false;
    }

    // Validate deliverables
    for (const deliverable of deliverables) {
      if (!deliverable?.deliverableName?.trim()) {
        setError('All deliverables must have a name');
        return false;
      }
      if (!deliverable?.dueDate) {
        setError('All deliverables must have a due date');
        return false;
      }
      if (!deliverable?.requiredUploadCount || deliverable?.requiredUploadCount < 1) {
        setError('All deliverables must have at least 1 required upload');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Update team basic info
      const updateData = {
        teamName: formData?.teamName?.trim(),
        teamNumber: parseInt(formData?.teamNumber),
        problemStatement: formData?.problemStatement?.trim(),
        mentor: formData?.mentor?.trim(),
        mentorEmail: formData?.mentorEmail?.trim(),
        cityOfficial: formData?.cityOfficial?.trim(),
        city: formData?.city?.trim(),
        category: formData?.category,
        projectDeadline: formData?.projectDeadline || null
      };

      await updateTeam(team?.id, updateData);

      // Handle deliverables
      for (const deliverable of deliverables) {
        if (deliverable?.isNew) {
          // Create new deliverable
          await createDeliverable({
            teamId: team?.id,
            deliverableName: deliverable?.deliverableName,
            dueDate: deliverable?.dueDate,
            requiredUploadCount: parseInt(deliverable?.requiredUploadCount) || 1
          });
        } else {
          // Update existing deliverable
          await updateDeliverable(deliverable?.id, {
            deliverableName: deliverable?.deliverableName,
            dueDate: deliverable?.dueDate,
            requiredUploadCount: parseInt(deliverable?.requiredUploadCount) || 1
          });
        }
      }
      
      setSuccessMessage('Team updated successfully!');
      setTimeout(() => {
        if (onTeamUpdated) onTeamUpdated();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error updating team:', err);
      setError(err?.message || 'Failed to update team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Edit Team</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update team information and configuration
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-600" />
                <p className="text-green-600 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Team Name"
                type="text"
                value={formData?.teamName}
                onChange={(e) => handleInputChange('teamName', e?.target?.value)}
                placeholder="Enter team name"
                required
              />
              <Input
                label="Team Number"
                type="number"
                value={formData?.teamNumber}
                onChange={(e) => handleInputChange('teamNumber', e?.target?.value)}
                placeholder="Enter team number"
                required
                min="1"
              />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Problem Statement
                </label>
                <textarea
                  value={formData?.problemStatement}
                  onChange={(e) => handleInputChange('problemStatement', e?.target?.value)}
                  placeholder="Enter problem statement"
                  rows="4"
                  className="w-full p-3 rounded-lg border border-border bg-background text-foreground resize-y"
                />
              </div>
            </div>
          </div>

          {/* Team Contacts */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Team Contacts</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Mentor"
                type="text"
                value={formData?.mentor}
                onChange={(e) => handleInputChange('mentor', e?.target?.value)}
                placeholder="Enter mentor name"
              />
              <Input
                label="Mentor's Email"
                type="email"
                value={formData?.mentorEmail}
                onChange={(e) => handleInputChange('mentorEmail', e?.target?.value)}
                placeholder="Enter mentor's email"
              />
              <Input
                label="City Official"
                type="text"
                value={formData?.cityOfficial}
                onChange={(e) => handleInputChange('cityOfficial', e?.target?.value)}
                placeholder="Enter city official"
              />
            </div>
          </div>

          {/* Project Details */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Project Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="City"
                type="text"
                value={formData?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                placeholder="Enter city"
              />
              <Select
                label="Category"
                value={formData?.category}
                onChange={(e) => handleInputChange('category', e?.target?.value)}
                options={categoryOptions}
              />
              <Input
                label="Project Deadline"
                type="date"
                value={formData?.projectDeadline}
                onChange={(e) => handleInputChange('projectDeadline', e?.target?.value)}
              />
            </div>
          </div>

          {/* Deliverables Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Deliverables</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddDeliverable}
                disabled={loading}
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Add Deliverable
              </Button>
            </div>

            {deliverables?.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                <Icon name="FileText" size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No deliverables added yet</p>
                <p className="text-sm text-muted-foreground mt-1">Click "Add Deliverable" to create one</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deliverables?.map((deliverable) => (
                  <div key={deliverable?.id} className="p-4 border border-border rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Input
                          label="Deliverable Name"
                          required
                          value={deliverable?.deliverableName}
                          onChange={(e) => handleDeliverableChange(deliverable?.id, 'deliverableName', e?.target?.value)}
                          placeholder="e.g., Problem Analysis"
                        />
                        <Input
                          label="Due Date"
                          type="date"
                          required
                          value={deliverable?.dueDate}
                          onChange={(e) => handleDeliverableChange(deliverable?.id, 'dueDate', e?.target?.value)}
                        />
                        <Input
                          label="Required Upload Count"
                          type="number"
                          min="1"
                          required
                          value={deliverable?.requiredUploadCount}
                          onChange={(e) => handleDeliverableChange(deliverable?.id, 'requiredUploadCount', e?.target?.value)}
                          placeholder="1"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveDeliverable(deliverable)}
                        className="ml-2 p-2 hover:bg-error/10 rounded-lg transition-colors"
                        disabled={loading}
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                    {!deliverable?.isNew && (
                      <div className="text-xs text-muted-foreground">
                        Progress: {deliverable?.completedUploadCount || 0} / {deliverable?.requiredUploadCount} uploads • Status: {deliverable?.status}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeamModal;